import { useTranslate, useGo, useNotification } from "@refinedev/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Alert, Flex, Input, Typography } from "antd";
import { LockIcon } from "@phosphor-icons/react";
import { Button } from "@libertify/ui";
import {
  answerMfaChallenge,
  getSafeRedirectPath,
  type AnswerMfaResult,
} from "@libertify/auth/refine-adapter";
import type { AuthChallenge, MfaFactor } from "@libertify/auth/core";
import { authService } from "services/authService";
import instance from "services/api";
import { trackEventWithoutHooks } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import { API_URL } from "@/constants";
import { detectNetworkError, isNetworkError } from "utils/network";
import type { NetworkErrorType } from "utils/network";
import { AuthPage, AuthRightPanel } from "./components/AuthLayout";
import { CoverflowCarousel } from "./components/CoverflowCarousel";
import { AuthFormPanel } from "./components/AuthFormPanel";
import {
  AuthLink,
  AuthOtpInput,
  AuthSecurityNote,
} from "./components/AuthFormElements";

type ChallengeState = {
  challenge: AuthChallenge;
  message?: string;
  available_factors?: MfaFactor[];
};

const FACTOR_LABEL_KEYS: Record<MfaFactor, string> = {
  Totp: "pages.auth.mfa-verify.factors.Totp",
  Sms: "pages.auth.mfa-verify.factors.Sms",
  Email: "pages.auth.mfa-verify.factors.Email",
};

export const MfaVerifyPage = () => {
  const t = useTranslate();
  const go = useGo();
  const { open: notify } = useNotification();

  // Preserve the deep-link target so we can send the user back where they
  // came from instead of always landing on "/".
  const [searchParams] = useSearchParams();
  const toParam = searchParams.get("to");
  const to = toParam ? getSafeRedirectPath(toParam) : null;
  const loginPath = to ? `/login?to=${encodeURIComponent(to)}` : "/login";

  // Lazy init reads the external authService session once, during render —
  // no cascading setState from an effect for the direct-navigation case.
  const [state, setState] = useState<ChallengeState | null>(() => {
    const mfaSession = authService.getMfaSession();
    return mfaSession
      ? {
          challenge: mfaSession.challenge as AuthChallenge,
          message: mfaSession.message,
          available_factors: mfaSession.available_factors,
        }
      : null;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invalidCode, setInvalidCode] = useState(false);
  const [networkError, setNetworkError] = useState<NetworkErrorType | null>(
    null
  );

  const restartTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Cancel the pending /login redirect if the user navigates away before it fires.
  useEffect(() => {
    return () => clearTimeout(restartTimeoutRef.current);
  }, []);

  const handleResult = useCallback(
    (result: AnswerMfaResult) => {
      if (result.done) {
        void trackEventWithoutHooks(AnalyticsEvents.Login, {
          login_method: "email",
        });
        go({ to: to || "/" });
        return;
      }

      if ("restart" in result) {
        // No MFA session to answer against (e.g. cleared by another tab) —
        // nothing to retry, so bounce back to /login.
        setInvalidCode(true);
        restartTimeoutRef.current = setTimeout(() => go({ to: loginPath }), 2000);
        return;
      }

      if ("challenge" in result) {
        setState({
          challenge: result.challenge,
          message: result.message,
          available_factors: result.available_factors,
        });
        return;
      }

      // Same discrimination login.tsx's onError does: a raw axios network
      // failure (no response) vs a real backend error (COGNITO_ERROR, ...).
      if (isNetworkError({ message: result.error })) {
        void detectNetworkError(API_URL || undefined).then(setNetworkError);
        return;
      }
      const options = {
        type: "error" as const,
        message: result.error || t("pages.auth.errors.loginFailed"),
        // Otherwise refine's default formatting wraps this in a generic
        // "There was an error creating..." CRUD-style title.
        skipFriendly: true,
      };
      notify?.(options);
    },
    [go, notify, t, to, loginPath]
  );

  const submitAnswer = useCallback(
    async (answer: MfaFactor) => {
      setIsSubmitting(true);
      setNetworkError(null);
      const result = await answerMfaChallenge(instance, authService, answer);
      setIsSubmitting(false);
      handleResult(result);
    },
    [handleResult]
  );

  // Direct navigation/reload after the MFA session was already cleared
  // (challenge answered, or it never existed) — nothing to answer here.
  if (!state) return <Navigate to="/login" />;

  return (
    <AuthPage>
      <CoverflowCarousel />
      <AuthRightPanel>
        <AuthFormPanel title={t("pages.auth.mfa-verify.title")}>
          {invalidCode ? (
            <Alert
              type="error"
              showIcon
              message={t("pages.auth.mfa-verify.sessionExpired")}
            />
          ) : (
            <>
              {state.message && (
                <Typography.Text
                  type="secondary"
                  style={{ textAlign: "center" }}
                >
                  {state.message}
                </Typography.Text>
              )}

              {state.challenge === "SELECT_MFA_TYPE" ? (
                <Flex vertical gap={8} style={{ width: "100%" }}>
                  {/* Email is a legacy factor we no longer offer for setup, but the
                      backend can still list it here for accounts enrolled before this
                      change — filter it out of the picker defensively. */}
                  {(state.available_factors ?? [])
                    .filter((factor) => factor !== "Email")
                    .map((factor) => (
                      <Button
                        key={factor}
                        variant="outlined"
                        size="l"
                        block
                        disabled={isSubmitting}
                        onClick={() => void submitAnswer(factor)}
                      >
                        {t(FACTOR_LABEL_KEYS[factor])}
                      </Button>
                    ))}
                </Flex>
              ) : (
                <AuthOtpInput>
                  <Input.OTP
                    key={state.challenge}
                    size="large"
                    disabled={isSubmitting}
                    onChange={(value) => {
                      if (!isSubmitting && value.length === 6) {
                        void submitAnswer(value as MfaFactor);
                      }
                    }}
                  />
                </AuthOtpInput>
              )}

              {networkError === "vpn" && (
                <Alert
                  type="error"
                  message={t("pages.login.errors.networkBlocked")}
                />
              )}
              {networkError === "offline" && (
                <Alert
                  type="warning"
                  message={t("pages.login.errors.offline")}
                />
              )}
              {networkError === "api_error" && (
                <Alert
                  type="warning"
                  message={t("pages.login.errors.apiUnreachable")}
                />
              )}
            </>
          )}

          <AuthLink
            onClick={() => go({ to: loginPath })}
            style={{
              display: "block",
              textAlign: "center",
              marginTop: 16,
              fontSize: 14,
            }}
          >
            ← {t("pages.auth.verification.backToLogin")}
          </AuthLink>

          <AuthSecurityNote>
            <LockIcon size={12} />
            {t("pages.auth.new-register.trustBadge")}
          </AuthSecurityNote>
        </AuthFormPanel>
      </AuthRightPanel>
    </AuthPage>
  );
};
